import type { DispatchTarget } from "@/modules/pos/components/dispatch/useFloorDispatch";
import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Route, Wallet } from "lucide-react";
import { useI18n, useTranslate } from "@/lib/i18n";
import type { DeliveryTabGridEntry, DriverSettlementCardView } from "@/lib/api/dispatchTypes";
import { countedKey } from "@/lib/i18n/plural";
import { useMoney } from "@/shared/ui";
import { clockTime } from "@/modules/pos/lib/dispatchBoard";
import { avatarTone, initials } from "@/modules/pos/lib/deliveryWaiting";
import {
  cardFlags,
  cardProgress,
  isSettledCard,
  LIVE_BAG_STATES,
  subjectKey,
  visibleSettlementCards,
} from "@/modules/pos/lib/driverSettlementCards";
import { SettleDriverPanel, subjectSeed } from "./SettleDriverPanel";

/** "#12" / "1234" kept left-to-right inside an Arabic line. */
const ltr = (text: string): string => `⁦${text}⁩`;

/**
 * «بانتظار التحصيل» — the Delivery tab's third lane (B6 · P25, plan D-41; a
 * lane in the 2026-10-26 redesign): one card per subject — an app driver
 * (Staff), a typed own rider, or a platform courier who collected cash
 * (Platform) — read from `GET restaurant/dispatch/settlements/open`.
 *
 * The lane head counts the cards still to settle and sums their cash. Each
 * card: avatar, name, «code · kind», «done / total», the cash to collect, the
 * bags that finished (delivered with how each was paid, returned, voided, or
 * still with a typed rider) — each opens its bill for a session that may — a
 * note while bags are still out, the returned / voided / left-owed chips, and
 * «Settle ⟨name⟩» — «View run» while a bag is still out, or for a session that
 * may not settle — which opens `SettleDriverPanel` (it also carries the
 * dispatch controls of every bag still with the driver). A settled card dims
 * to the end with its «Settled HH:MM».
 */
export function DriverSettlementsCard({
  cards,
  loading,
  error,
  onRetry,
  bills,
  dispatchAction,
  canOpen,
  canSettle,
  canPayOut,
  canViewProof,
}: {
  cards: readonly DriverSettlementCardView[] | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  bills: readonly DeliveryTabGridEntry[];
  dispatchAction: (bill: DispatchTarget) => ReactNode;
  canOpen: boolean;
  /** `DISPATCH_CASH_CONFIRM` and `INVOICE_SETTLE` (the command's pair). */
  canSettle: boolean;
  /** `SHIFT_MANAGE`: the optional pay-out to a rider. */
  canPayOut: boolean;
  /** `DISPATCH_CASH_CONFIRM`: open an InstaPay screenshot. */
  canViewProof: boolean;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const money = useMoney();
  const navigate = useNavigate();
  const { locale: routeLocale = "ar" } = useParams();
  const [openKey, setOpenKey] = useState<string | null>(null);
  /** The card as it was when last seen, so the panel survives the card leaving the list. */
  const [openSnapshot, setOpenSnapshot] = useState<DriverSettlementCardView | null>(null);

  const nameOf = (card: DriverSettlementCardView) => card.name.trim() || t((m) => m.riderSitting.unnamed);
  const all = cards ?? [];
  // Unsettled first (most cash first), the settled ones dimmed at the end.
  const { shown } = visibleSettlementCards(all, "all", "", nameOf, locale);
  const toSettle = all.filter((card) => !isSettledCard(card));
  const cashTotal = toSettle.reduce((sum, card) => sum + card.sums.cashExpected, 0);
  const liveOpen = openKey ? all.find((card) => subjectKey(card.subject) === openKey) : undefined;
  const openCard = liveOpen ?? (openKey ? openSnapshot : null);
  const title = t((m) => m.dispatch.laneCollectTitle);

  const chip = (card: DriverSettlementCardView) =>
    card.subject.kind === "DRIVER"
      ? t((m) => m.dispatch.settleChipStaff)
      : card.subject.kind === "PLATFORM"
        ? t((m) => m.dispatch.settleChipPlatform)
        : t((m) => m.dispatch.settleChipRider);
  const code = (card: DriverSettlementCardView): string | null =>
    card.subject.kind === "DRIVER"
      ? card.code
        ? ltr(card.code)
        : null
      : card.subject.kind === "PLATFORM"
        ? card.aggregatorName
        : null;
  const bagState = (bag: DriverSettlementCardView["bags"][number]) =>
    bag.state === "RETURNED"
      ? { label: t((m) => m.dispatch.laneBagReturned), tone: "red" }
      : bag.state === "VOIDED"
        ? { label: t((m) => m.dispatch.laneBagVoided), tone: "grey" }
        : bag.state === "OUT_WITH_RIDER"
          ? { label: t((m) => m.dispatch.settleStateWithRider), tone: "amber" }
          : bag.due > 0
          ? { label: t((m) => m.dispatch.laneBagDeliveredCash), tone: "green" }
          : { label: t((m) => m.dispatch.laneBagDeliveredPaid), tone: "green" };

  return (
    <section data-ui="dl-lane" data-lane="collect" data-testid="settlements-card" aria-label={title} className="flex min-w-0 flex-col">
      <header data-ui="dl-lane-head" className="flex items-center">
        <span aria-hidden="true" data-ui="dl-dot" data-tone="green" />
        <h2 data-ui="dl-lane-title">{title}</h2>
        {cards ? (
          <span data-ui="dl-count" data-testid="settlements-count" className="tabular-nums">
            {toSettle.length}
          </span>
        ) : null}
        <span className="flex-1" />
        {cards && cashTotal > 0 ? (
          <span data-ui="dl-money" data-testid="settlements-cash-total" className="tabular-nums">
            {money(cashTotal)}
          </span>
        ) : null}
      </header>
      <p data-ui="dl-lane-hint">{t((m) => m.dispatch.laneCollectHint)}</p>

      {loading && !cards ? (
        <div data-ui="waiting-loading" aria-busy="true" />
      ) : error && !cards ? (
        <p data-ui="waiting-error" role="alert" data-testid="settlements-error" className="flex flex-wrap items-center">
          <span>{t((m) => m.dispatch.settleLoadFailed)}</span>
          <button type="button" data-ui="waiting-retry" onClick={onRetry}>
            {t((m) => m.common.retry)}
          </button>
        </p>
      ) : shown.length === 0 ? (
        <p data-ui="dl-empty" data-testid="settlements-empty" className="flex items-center justify-center">
          <Check aria-hidden="true" size={16} strokeWidth={2.25} />
          <span>{t((m) => m.dispatch.laneCollectEmpty)}</span>
        </p>
      ) : (
        <div data-ui="dl-cards" role="list" aria-label={title} className="flex flex-col">
          {shown.map((card) => {
            const key = subjectKey(card.subject);
            const settled = isSettledCard(card);
            const progress = cardProgress(card);
            const flags = cardFlags(card);
            const name = nameOf(card);
            const cardCode = code(card);
            // Everything not still on an app driver's run: finished bags and a typed
            // rider's bags (no app, so they stay «with the rider» until settled).
            const ended = settled ? [] : card.bags.filter((bag) => !LIVE_BAG_STATES.has(bag.state));
            const out = settled ? 0 : card.counts.onTheWay + card.counts.waitingPickup;
            return (
              <article
                key={key}
                role="listitem"
                data-ui="dl-card"
                data-settled={settled ? "" : undefined}
                data-selected={openKey === key ? "" : undefined}
                data-kind={card.subject.kind}
                data-testid={`settlement-${card.subject.kind}-${card.subject.driverSellerId ?? card.subject.riderKey ?? ""}`}
                className="flex flex-col"
              >
                <div data-ui="dl-row" className="flex items-center">
                  <span data-ui="settle-avatar" data-tone={avatarTone(subjectSeed(card))} aria-hidden="true">
                    {initials(name)}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span data-ui="dl-name" className="truncate">
                      <bdi>{name}</bdi>
                    </span>
                    <span data-ui="dl-sub" className="flex min-w-0 items-center gap-1">
                      {cardCode ? (
                        <>
                          <span className="truncate">
                            <bdi>{cardCode}</bdi>
                          </span>
                          <span aria-hidden="true">·</span>
                        </>
                      ) : null}
                      <span data-ui="settle-kind" data-kind={card.subject.kind}>
                        {chip(card)}
                      </span>
                    </span>
                  </span>
                  {progress.total > 0 ? (
                    <span data-ui="dl-age" data-testid="settlement-progress" className="tabular-nums">
                      <bdi dir="ltr">
                        {progress.done} / {progress.total}
                      </bdi>
                    </span>
                  ) : null}
                </div>
                <div data-ui="dl-row" className="flex items-baseline">
                  <span data-ui="dl-sub">{t((m) => m.dispatch.laneCollectLabel)}</span>
                  <span className="flex-1" />
                  <span data-ui="dl-cash" data-testid="settlement-cash" className="tabular-nums">
                    {money(card.sums.cashExpected)}
                  </span>
                </div>
                {ended.length > 0 ? (
                  <div data-ui="dl-bags" className="flex flex-col">
                    {ended.map((bag) => {
                      const state = bagState(bag);
                      const Row = canOpen ? "button" : "div";
                      return (
                        <Row
                          key={bag.invoiceId}
                          {...(canOpen
                            ? {
                                type: "button" as const,
                                onClick: () => navigate(`/${routeLocale}/pos/invoices/${bag.invoiceId}`),
                                "aria-label": t((m) => m.dispatch.pickupOpenBillAria, { sequence: bag.orderNumber }),
                                "data-open": "",
                              }
                            : {})}
                          data-ui="dl-bag"
                          data-testid={`settlement-bag-${bag.orderNumber}`}
                          className="flex items-center text-start"
                        >
                          <span data-ui="dl-bag-no" className="tabular-nums">
                            <bdi dir="ltr">#{bag.orderNumber}</bdi>
                          </span>
                          <span data-ui="dl-pay" className="min-w-0 truncate">
                            <bdi>{bag.customerName?.trim() || t((m) => m.dispatch.waitingNoName)}</bdi>
                          </span>
                          <span className="flex-1" />
                          <span data-ui="dl-pill" data-tone={state.tone}>
                            {state.label}
                          </span>
                          <span data-ui="dl-money" data-size="sm" className="tabular-nums">
                            {bag.due > 0 && bag.state !== "RETURNED" && bag.state !== "VOIDED" ? money(bag.due) : "—"}
                          </span>
                        </Row>
                      );
                    })}
                  </div>
                ) : null}
                {out > 0 ? (
                  <span data-ui="dl-out" data-testid="settlement-out">
                    {t(
                      countedKey(out, {
                        one: (m) => m.dispatch.laneOutOne,
                        two: (m) => m.dispatch.laneOutTwo,
                        few: (m) => m.dispatch.laneOutFew,
                        many: (m) => m.dispatch.laneOut,
                      }),
                      { count: out },
                    )}
                  </span>
                ) : null}
                <div data-ui="settle-flags" className="flex flex-wrap items-center">
                  {flags.length === 0 ? (
                    <span data-ui="settle-flags-none">{t((m) => m.dispatch.settleNoFlags)}</span>
                  ) : (
                    flags.map((flag) => (
                      <span
                        key={`${flag.kind}-${flag.orderNumber}`}
                        data-ui="settle-flag"
                        data-kind={flag.kind}
                        data-testid={`settlement-flag-${flag.orderNumber}`}
                      >
                        {flag.kind === "RETURNED"
                          ? t((m) => m.dispatch.settleFlagReturned, { number: flag.orderNumber })
                          : flag.kind === "VOIDED"
                            ? t((m) => m.dispatch.settleFlagVoided, { number: flag.orderNumber })
                            : t((m) => m.dispatch.settleFlagLeftOwed, { number: flag.orderNumber })}
                      </span>
                    ))
                  )}
                </div>
                {settled ? (
                  <span data-ui="dl-pill" data-tone="green" data-testid="settlement-settled" className="self-start">
                    {t((m) => m.dispatch.settleSettledAt, {
                      time: ltr(clockTime(card.lastSettledToday?.confirmedAt ?? "", locale)),
                    })}
                  </span>
                ) : (
                  <button
                    type="button"
                    data-ui="dl-btn"
                    data-wide=""
                    data-tone={out > 0 || !canSettle ? "quiet" : "primary"}
                    data-testid="settlement-open"
                    aria-label={canSettle ? t((m) => m.dispatch.settleButtonAria, { name }) : undefined}
                    onClick={() => {
                      setOpenKey(key);
                      setOpenSnapshot(card);
                    }}
                    className="flex items-center justify-center"
                  >
                    {out > 0 ? (
                      <Route aria-hidden="true" size={14} strokeWidth={2} />
                    ) : (
                      <Wallet aria-hidden="true" size={14} strokeWidth={2} />
                    )}
                    <span>
                      {canSettle && out === 0
                        ? t((m) => m.dispatch.laneSettleNamed, { name })
                        : t((m) => m.dispatch.settleViewRun)}
                    </span>
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      {openCard ? (
        <SettleDriverPanel
          key={openKey ?? undefined}
          card={openCard}
          gone={cards !== undefined && !liveOpen}
          displayName={nameOf(openCard)}
          bills={bills}
          dispatchAction={dispatchAction}
          canOpen={canOpen}
          canSettle={canSettle}
          canPayOut={canPayOut}
          canViewProof={canViewProof}
          onClose={() => {
            setOpenKey(null);
            setOpenSnapshot(null);
          }}
        />
      ) : null}
    </section>
  );
}
